import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  searchEventsMeili,
  searchTracksMeili,
} from "@/server/meilisearch/meilisearchServer";
import { searchProfilesMeili } from "@/server/profiles/profilesServer";

const PER_GROUP_LIMIT = 5;

const groupMeta = {
  event: { label: "Events", routeType: "publication", publicationType: "Event" },
  track: { label: "Tracks", routeType: "publication", publicationType: "Track" },
  profile: { label: "Profiles", routeType: "profile" },
};

const buildHref = (group, id) => {
  if (group === "profile") return `/detail-profile/${id}`;
  return `/detail-publication/${id}?publicationType=${groupMeta[group].publicationType}`;
};

const mapEvent = (h) => ({
  id: h.id || h._id,
  title: h.title || "(untitled event)",
  subtitle: [h.venue, h["localisation.address"]].filter(Boolean).join(" · "),
  image: h.flyer || h.image || null,
});

const mapTrack = (h) => ({
  id: h.id || h._id,
  title: h.title || "(untitled track)",
  subtitle: [h.artist, h.label].filter(Boolean).join(" · "),
  image: h.flyer || h.cover || null,
});

const mapProfile = (h) => ({
  id: h.id || h._id,
  title: h.username || "(no name)",
  subtitle: [h.profileType, (h.musicTypeNames || []).slice(0, 2).join(", ")]
    .filter(Boolean)
    .join(" · "),
  image: h.profilePicture || h.picture || null,
});

const HeaderSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState({ event: [], track: [], profile: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!debounced) {
      setResults({ event: [], track: [], profile: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.allSettled([
      searchEventsMeili(debounced, { limit: PER_GROUP_LIMIT }),
      searchTracksMeili(debounced, { limit: PER_GROUP_LIMIT }),
      searchProfilesMeili(debounced, { limit: PER_GROUP_LIMIT, isActive: true }),
    ])
      .then(([events, tracks, profiles]) => {
        if (cancelled) return;
        setResults({
          event:
            events.status === "fulfilled" ? (events.value.data || []).map(mapEvent) : [],
          track:
            tracks.status === "fulfilled" ? (tracks.value.data || []).map(mapTrack) : [],
          profile:
            profiles.status === "fulfilled"
              ? (profiles.value.data || []).map(mapProfile)
              : [],
        });
        setActiveIndex(-1);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const flatItems = React.useMemo(() => {
    const out = [];
    ["event", "track", "profile"].forEach((g) => {
      results[g].forEach((item) => out.push({ ...item, group: g }));
    });
    return out;
  }, [results]);

  const pick = (item) => {
    setOpen(false);
    setQuery("");
    setDebounced("");
    navigate(buildHref(item.group, item.id));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || flatItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeIndex >= 0 ? activeIndex : 0];
      if (item) pick(item);
    }
  };

  const hasResults = flatItems.length > 0;
  const showDropdown = open && debounced.length > 0;

  let runningIndex = -1;

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-md"
      onFocus={() => setOpen(true)}
    >
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          {loading ? <LoadingOutlined /> : <SearchOutlined />}
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search publications, profiles…"
          className="h-10 w-full rounded-xl border border-stone-200 bg-stone-50 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-900"
        />
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-12 z-[1000] max-h-[28rem] overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
          {loading && !hasResults && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              Searching…
            </div>
          )}
          {!loading && !hasResults && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              No results for “{debounced}”
            </div>
          )}
          {["event", "track", "profile"].map((group) => {
            const items = results[group];
            if (!items.length) return null;
            return (
              <div key={group} className="py-1">
                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {groupMeta[group].label}
                </div>
                <ul>
                  {items.map((item) => {
                    runningIndex += 1;
                    const isActive = runningIndex === activeIndex;
                    return (
                      <li key={`${group}-${item.id}`}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIndex(runningIndex)}
                          onClick={() => pick({ ...item, group })}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                            isActive
                              ? "bg-teal-50 dark:bg-slate-800"
                              : "hover:bg-stone-50 dark:hover:bg-slate-800/60"
                          }`}
                        >
                          <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md bg-stone-200 dark:bg-slate-700">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="flex h-full w-full items-center justify-center text-xs font-semibold uppercase text-slate-500">
                                {(item.title || "?").slice(0, 1)}
                              </span>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                              {item.title}
                            </span>
                            {item.subtitle && (
                              <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                                {item.subtitle}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
